/**
 * useFileSystem Hook
 * 使用 File System Access API 实现本地文件夹扫描、读取、修改、重命名
 * 所有操作完全在浏览器本地进行，不涉及服务器
 */
import { API_BASE_URL } from '../services/api';

// ─── Types ─────────────────────────────────

export interface FileNode {
    name: string;
    kind: 'file' | 'directory';
    handle: FileSystemFileHandle | FileSystemDirectoryHandle;
    children?: FileNode[];
    path: string; // 相对路径
    lastModified?: number; // 文件最后修改时间戳
}

// AI 文件标签
export interface AiFileTag {
    title: string;      // 真实标题
    type: string;       // 文档类型（合同/诉状/录音等）
    summary: string;    // 30字核心摘要
    entities: string[]; // 关键实体（人名/公司）
    indexedAt: string;  // 索引时间
    fileLastModified?: number; // 文件最后修改时间戳（用于增量索引）
}

// AI 索引数据结构
export interface AiIndex {
    version: number;
    files: Record<string, AiFileTag>; // key = 文件相对路径
}

// ─── IndexedDB 持久化 ─────────────────────

const DB_NAME = 'lawhelper-fs';
const STORE_NAME = 'dir-handles';
const HANDLE_KEY = 'lastDirectory';

function openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = () => {
            request.result.createObjectStore(STORE_NAME);
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

export async function saveDirectoryHandle(handle: FileSystemDirectoryHandle): Promise<void> {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(handle, HANDLE_KEY);
    return new Promise((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    });
}

export async function restoreDirectoryHandle(): Promise<FileSystemDirectoryHandle | null> {
    try {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const request = tx.objectStore(STORE_NAME).get(HANDLE_KEY);
        return new Promise((resolve) => {
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => resolve(null);
        });
    } catch {
        return null;
    }
}

// ─── File System Access API ────────────────

/**
 * 打开目录选择器，让用户选择本地文件夹
 */
export async function openFolder(): Promise<FileSystemDirectoryHandle | null> {
    try {
        // @ts-ignore — showDirectoryPicker 尚未在所有 TS 版本中定义
        const handle: FileSystemDirectoryHandle = await window.showDirectoryPicker({
            mode: 'readwrite',
        });
        await saveDirectoryHandle(handle);
        return handle;
    } catch (err: any) {
        // 用户取消了选择
        if (err?.name === 'AbortError') return null;
        console.error('[useFileSystem] openFolder error:', err);
        return null;
    }
}

/**
 * 递归扫描目录，返回文件树
 */
export async function scanDirectory(
    dirHandle: FileSystemDirectoryHandle,
    parentPath: string = '',
    maxDepth: number = 5,
    currentDepth: number = 0,
): Promise<FileNode[]> {
    if (currentDepth >= maxDepth) return [];

    const nodes: FileNode[] = [];

    // @ts-ignore — entries() 在某些 TS 版本中未定义
    for await (const [name, handle] of dirHandle.entries()) {
        // 跳过隐藏文件和 node_modules
        if (name.startsWith('.') || name === 'node_modules' || name === '__pycache__') {
            continue;
        }

        const path = parentPath ? `${parentPath}/${name}` : name;

        if (handle.kind === 'directory') {
            const children = await scanDirectory(
                handle as FileSystemDirectoryHandle,
                path,
                maxDepth,
                currentDepth + 1,
            );
            nodes.push({
                name,
                kind: 'directory',
                handle,
                children,
                path,
            });
        } else {
            let lastModified: number | undefined;
            try {
                const file = await (handle as FileSystemFileHandle).getFile();
                lastModified = file.lastModified;
            } catch { /* ignore */ }
            nodes.push({
                name,
                kind: 'file',
                handle,
                path,
                lastModified,
            });
        }
    }

    // 排序：文件夹在前，文件在后，按名称排序
    nodes.sort((a, b) => {
        if (a.kind !== b.kind) return a.kind === 'directory' ? -1 : 1;
        return a.name.localeCompare(b.name);
    });

    return nodes;
}

// ─── Simple File Tree Listing ──────────────

/**
 * Build a plain text listing of the file tree for AI context.
 * Example:
 *   📁 contracts/
 *     📄 lease.pdf
 *     📄 employment.docx
 *   📃 notes.md
 */
export function buildFileTreeListing(
    nodes: FileNode[],
    depth: number = 0,
    aiIndex?: AiIndex | null
): string {
    const lines: string[] = [];
    for (const node of nodes) {
        const indent = '  '.repeat(depth);
        const icon = node.kind === 'directory' ? '📁' : getFileEmoji(node.name);
        lines.push(`${indent}${icon} ${node.name}`);

        // 如果有 AI 标签，附加到文件名下方
        if (aiIndex && node.kind === 'file' && aiIndex.files[node.path]) {
            const tag = aiIndex.files[node.path];
            const tagIndent = '  '.repeat(depth + 1);
            const entitiesStr = tag.entities.length > 0 ? ` | 实体: ${tag.entities.join(', ')}` : '';
            lines.push(`${tagIndent}↳ 智能标签: [${tag.type}] ${tag.title}${entitiesStr} | ${tag.summary}`);
        }

        if (node.children && node.children.length > 0) {
            lines.push(buildFileTreeListing(node.children, depth + 1, aiIndex));
        }
    }
    return lines.join('\n');
}

/**
 * 读取文件内容（文本）
 */
export async function readFileContent(handle: FileSystemFileHandle): Promise<string> {
    const file = await handle.getFile();
    return await file.text();
}

/**
 * 获取文件元信息
 */
export async function getFileInfo(handle: FileSystemFileHandle): Promise<{
    name: string;
    size: number;
    type: string;
    lastModified: number;
}> {
    const file = await handle.getFile();
    return {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
    };
}

/**
 * 写入文件内容
 */
export async function writeFileContent(
    handle: FileSystemFileHandle,
    content: string,
): Promise<void> {
    // @ts-ignore — createWritable 在某些 TS 版本中未定义
    const writable = await handle.createWritable();
    await writable.write(content);
    await writable.close();
}

/**
 * 重命名文件（在同一目录下创建新文件 + 复制内容 + 删除旧文件）
 */
export async function renameFile(
    dirHandle: FileSystemDirectoryHandle,
    oldName: string,
    newName: string,
): Promise<FileSystemFileHandle | null> {
    try {
        // 读取旧文件内容
        const oldHandle = await dirHandle.getFileHandle(oldName);
        const file = await oldHandle.getFile();
        const content = await file.arrayBuffer();

        // 创建新文件并写入内容
        const newHandle = await dirHandle.getFileHandle(newName, { create: true });
        // @ts-ignore
        const writable = await newHandle.createWritable();
        await writable.write(content);
        await writable.close();

        // 删除旧文件
        // @ts-ignore — removeEntry 在某些 TS 版本中未定义
        await dirHandle.removeEntry(oldName);

        return newHandle;
    } catch (err) {
        console.error('[useFileSystem] renameFile error:', err);
        return null;
    }
}

/**
 * 通过路径字符串从本地目录 Handle 中读取指定文件的内容
 * 支持嵌套路径，例如 "contracts/lease.pdf" 或 "notes.md"
 */
export async function readFileByPath(
    dirHandle: FileSystemDirectoryHandle,
    filePath: string
): Promise<string | null> {
    try {
        const parts = filePath.split('/').filter(Boolean);
        let currentHandle: FileSystemDirectoryHandle = dirHandle;

        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (i === parts.length - 1) {
                // 最后一个部分是文件
                const fileHandle = await currentHandle.getFileHandle(part);
                return await readFileContent(fileHandle);
            } else {
                // 中间部分是目录
                currentHandle = await currentHandle.getDirectoryHandle(part);
            }
        }
        return null; // 不应到达这里
    } catch (err) {
        console.error(`[useFileSystem] Failed to read file at path: ${filePath}`, err);
        return null;
    }
}

// ─── AI Index (.ai_index.json) Management ──

const AI_INDEX_FILENAME = '.ai_index.json';

/**
 * 读取本地文件夹中的 .ai_index.json
 */
export async function readAiIndex(
    dirHandle: FileSystemDirectoryHandle
): Promise<AiIndex> {
    try {
        const fileHandle = await dirHandle.getFileHandle(AI_INDEX_FILENAME);
        const file = await fileHandle.getFile();
        const text = await file.text();
        const data = JSON.parse(text);
        return {
            version: data.version || 1,
            files: data.files || {},
        };
    } catch {
        // 文件不存在，返回空索引
        return { version: 1, files: {} };
    }
}

/**
 * 保存 .ai_index.json 到本地文件夹
 */
export async function writeAiIndex(
    dirHandle: FileSystemDirectoryHandle,
    index: AiIndex
): Promise<void> {
    try {
        const fileHandle = await dirHandle.getFileHandle(AI_INDEX_FILENAME, { create: true });
        // @ts-ignore
        const writable = await fileHandle.createWritable();
        await writable.write(JSON.stringify(index, null, 2));
        await writable.close();
        console.log(`[useFileSystem] ✅ AI Index saved (${Object.keys(index.files).length} files)`);
    } catch (err) {
        console.error('[useFileSystem] Failed to save AI Index:', err);
    }
}

/**
 * 对单个文件调用后端 /api/ai-indexer 生成智能标签
 */
export async function generateTagsForFile(
    dirHandle: FileSystemDirectoryHandle,
    filePath: string
): Promise<AiFileTag | null> {
    try {
        const fileName = filePath.split('/').pop() || filePath;
        const isBin = isBinaryFile(fileName);

        let requestBody: any;

        if (isBin) {
            // 压缩包：Gemini 无法解析，直接跳过
            const ext = fileName.split('.').pop()?.toLowerCase() || '';
            if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
                console.log(`[useFileSystem] ⏭️ Skipping archive: ${filePath}`);
                return null;
            }

            // 二进制文件：读取 Base64
            const fileData = await readFileAsBase64(dirHandle, filePath);
            if (!fileData) return null;

            // 对于大文件（>5MB），只取部分以节省时间
            let base64 = fileData.base64;
            if (fileData.size > 5 * 1024 * 1024) {
                // 只取前 1MB 的 Base64（约 1.3MB 字符串）
                const partialLength = Math.floor((1 * 1024 * 1024) / 3 * 4);
                base64 = base64.substring(0, partialLength);
                console.log(`[useFileSystem] Large file, using partial Base64 (${(partialLength/1024).toFixed(0)}KB)`);
            }

            requestBody = {
                base64,
                fileName: fileData.fileName,
                mimeType: fileData.mimeType,
            };
        } else {
            // 文本文件：直接读取
            const text = await readFileByPath(dirHandle, filePath);
            if (!text) return null;

            requestBody = {
                text: text.substring(0, 3000), // 取前 3000 字符
                fileName,
                mimeType: 'text/plain',
            };
        }

        const apiBase = API_BASE_URL;
        const token = localStorage.getItem('auth_token');

        const response = await fetch(`${apiBase}/api/ai-indexer`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
            console.warn(`[useFileSystem] AI Indexer failed for ${filePath}: ${response.statusText}`);
            return null;
        }

        const result = await response.json();
        if (result.success && result.tags) {
            console.log(`[useFileSystem] 🏷️ Tagged: ${filePath} → [${result.tags.type}] ${result.tags.title}`);
            return result.tags;
        }
        return null;
    } catch (err) {
        console.error(`[useFileSystem] generateTagsForFile error for ${filePath}:`, err);
        return null;
    }
}

/**
 * 批量为所有文件生成 AI 标签（文本 + 二进制并行处理）
 * - 文本文件：扁平合并，每批最多 200 个，filePath 携带文件夹上下文
 * - 二进制文件（PDF/图片/音视频）：并行上传到 Google File API，每批 10 个一次 Gemini 调用
 * - 文本批次与二进制批次并行执行
 */
export async function generateTagsBatch(
    dirHandle: FileSystemDirectoryHandle,
    filePaths: string[],
    lastModifiedMap: Record<string, number>,
    onProgress?: (current: number, total: number) => void,
): Promise<Record<string, AiFileTag>> {
    const results: Record<string, AiFileTag> = {};
    const binaryFiles: string[] = [];
    const textFiles: { path: string; text: string }[] = [];

    // ─── 每文件状态追踪（防止多批次混乱） ───
    type FileStatus = 'pending' | 'batch_ok' | 'batch_miss' | 'retry_ok' | 'retry_fail' | 'skipped';
    const tracker = new Map<string, FileStatus>();
    for (const fp of filePaths) {
        const ext = (fp.split('.').pop() || '').toLowerCase();
        tracker.set(fp, ['zip', 'rar', '7z', 'tar', 'gz'].includes(ext) ? 'skipped' : 'pending');
    }

    // 分类：文本 vs 二进制（扁平收集，不按文件夹分组）
    for (const filePath of filePaths) {
        const fileName = filePath.split('/').pop() || filePath;

        if (isBinaryFile(fileName)) {
            binaryFiles.push(filePath);
        } else {
            try {
                const text = await readFileByPath(dirHandle, filePath);
                if (text) {
                    textFiles.push({ path: filePath, text: text.substring(0, 3000) });
                }
            } catch { tracker.set(filePath, 'skipped'); }
        }
    }

    const total = filePaths.length;
    let done = 0;
    const MAX_BATCH = 200;

    // 安全写入 results + 更新 tracker（统一入口，防混乱）
    const recordResult = (filePath: string, tags: AiFileTag, source: 'batch_ok' | 'retry_ok') => {
        tags.fileLastModified = lastModifiedMap[filePath] || Date.now();
        tags.indexedAt = new Date().toISOString();
        results[filePath] = tags;
        tracker.set(filePath, source);
    };

    // ─── 文本文件：扁平批量发送（filePath 携带文件夹上下文） ───
    const textBatchPromise = (async () => {
        for (let i = 0; i < textFiles.length; i += MAX_BATCH) {
            const batch = textFiles.slice(i, i + MAX_BATCH);
            try {
                const apiBase = API_BASE_URL;
                const token = localStorage.getItem('auth_token');
                const response = await fetch(`${apiBase}/api/ai-indexer/batch`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({
                        files: batch.map(f => ({
                            filePath: f.path,
                            text: f.text,
                        })),
                    }),
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.success && data.results) {
                        // 用发送时的 filePath 列表做校验，只接受本批次的文件
                        const batchPaths = new Set(batch.map(f => f.path));
                        for (const [path, tags] of Object.entries(data.results)) {
                            if (batchPaths.has(path)) {
                                recordResult(path, tags as AiFileTag, 'batch_ok');
                            }
                        }
                    }
                }
            } catch (err) {
                console.warn(`[useFileSystem] Batch indexing error:`, err);
            }
            done += batch.length;
            onProgress?.(done, total);
        }
    })();

    // ─── 二进制文件：逐个上传拿 URI → 按类型分组 → 批量分析（与文本并行） ───
    // 上传和分析分离：每个上传请求仅 ~1.3MB，分析请求只传 URI（几 KB）
    // 视频：Gemini 限制每次最多 10 个；图片/PDF/音频：每批 200 个（URI 无体积瓶颈）
    const VIDEO_EXTS = ['mp4', 'mov', 'avi', 'webm', 'mkv'];
    const MAX_VIDEO_ANALYZE = 10;
    const MAX_OTHER_ANALYZE = 200;

    const binaryBatchPromise = (async () => {
        type UploadedItem = { filePath: string; fileUri: string; mimeType: string };
        const videoUploaded: UploadedItem[] = [];
        const otherUploaded: UploadedItem[] = [];
        const apiBase = API_BASE_URL;
        const token = localStorage.getItem('auth_token');
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        };

        // 1. 并行读取 + 逐个上传到 Google File API（并发 10）
        const UPLOAD_CONCURRENCY = 10;
        const uploadQueue: string[] = [];
        for (const filePath of binaryFiles) {
            const ext = (filePath.split('.').pop() || '').toLowerCase();
            if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) continue;
            uploadQueue.push(filePath);
        }

        for (let i = 0; i < uploadQueue.length; i += UPLOAD_CONCURRENCY) {
            const chunk = uploadQueue.slice(i, i + UPLOAD_CONCURRENCY);
            const uploadResults = await Promise.all(chunk.map(async (filePath) => {
                try {
                    const fileData = await readFileAsBase64(dirHandle, filePath);
                    if (!fileData) return null;
                    let base64 = fileData.base64;
                    if (fileData.size > 5 * 1024 * 1024) {
                        const partialLength = Math.floor((1 * 1024 * 1024) / 3 * 4);
                        base64 = base64.substring(0, partialLength);
                    }

                    const resp = await fetch(`${apiBase}/api/ai-indexer/upload`, {
                        method: 'POST',
                        headers,
                        body: JSON.stringify({
                            base64,
                            fileName: fileData.fileName,
                            mimeType: fileData.mimeType,
                        }),
                    });

                    if (resp.ok) {
                        const data = await resp.json();
                        if (data.success && data.fileUri) {
                            return { filePath, fileUri: data.fileUri as string, mimeType: fileData.mimeType };
                        }
                    }
                    return null;
                } catch { return null; }
            }));

            for (const r of uploadResults) {
                if (!r) continue;
                const ext = (r.filePath.split('.').pop() || '').toLowerCase();
                if (VIDEO_EXTS.includes(ext)) {
                    videoUploaded.push(r);
                } else {
                    otherUploaded.push(r);
                }
            }
            done += chunk.length;
            onProgress?.(done, total);
        }

        // 2. 批量分析（只传 URI，请求体几 KB）
        const sendAnalyzeBatch = async (batch: UploadedItem[]) => {
            try {
                const batchPaths = new Set(batch.map(f => f.filePath));
                const resp = await fetch(`${apiBase}/api/ai-indexer/batch-analyze`, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify({ files: batch }),
                });
                if (resp.ok) {
                    const data = await resp.json();
                    if (data.success && data.results) {
                        for (const [path, tags] of Object.entries(data.results)) {
                            if (batchPaths.has(path)) {
                                recordResult(path, tags as AiFileTag, 'batch_ok');
                            }
                        }
                    }
                }
            } catch (err) {
                console.warn(`[useFileSystem] Batch analyze error:`, err);
            }
        };

        // 视频：每批 10 个
        for (let i = 0; i < videoUploaded.length; i += MAX_VIDEO_ANALYZE) {
            await sendAnalyzeBatch(videoUploaded.slice(i, i + MAX_VIDEO_ANALYZE));
        }
        // 图片/PDF/音频：每批 200 个
        for (let i = 0; i < otherUploaded.length; i += MAX_OTHER_ANALYZE) {
            await sendAnalyzeBatch(otherUploaded.slice(i, i + MAX_OTHER_ANALYZE));
        }
        // 跳过的压缩包计入进度
        done += binaryFiles.length - uploadQueue.length;
        onProgress?.(done, total);
    })();

    // 并行等待文本批次和二进制处理
    await Promise.all([textBatchPromise, binaryBatchPromise]);

    // ─── 自动降级：tracker 中仍为 pending 的文件逐个重试 ───
    const missing = Array.from(tracker.entries())
        .filter(([_, status]) => status === 'pending')
        .map(([fp]) => fp);

    if (missing.length > 0) {
        console.log(`[useFileSystem] 🔄 Batch missed ${missing.length} files, retrying individually...`);
        const RETRY_CONCURRENCY = 3;
        for (let i = 0; i < missing.length; i += RETRY_CONCURRENCY) {
            const chunk = missing.slice(i, i + RETRY_CONCURRENCY);
            await Promise.all(chunk.map(async (filePath) => {
                try {
                    const tags = await generateTagsForFile(dirHandle, filePath);
                    if (tags) {
                        recordResult(filePath, tags, 'retry_ok');
                    } else {
                        tracker.set(filePath, 'retry_fail');
                    }
                } catch (err) {
                    tracker.set(filePath, 'retry_fail');
                    console.warn(`[useFileSystem] Retry failed for ${filePath}:`, err);
                }
            }));
        }
    }

    // ─── 统计 ───
    let success = 0, failed = 0, skipped = 0;
    for (const [_, status] of Array.from(tracker.entries())) {
        if (status === 'batch_ok' || status === 'retry_ok') success++;
        else if (status === 'retry_fail') failed++;
        else if (status === 'skipped') skipped++;
        // 'pending' 不应出现在此处，但如果有则算 failed
        else if (status === 'pending') failed++;
    }

    console.log(`[useFileSystem] 📊 Indexing complete: ${success} ok, ${failed} failed, ${skipped} skipped / ${tracker.size} total`);

    return results;
}

/**
 * 获取需要索引的文件路径（新文件 + 修改过的文件）
 * 排除 .ai_index.json 和隐藏文件
 */
export function getFilesToIndex(nodes: FileNode[], existingIndex: AiIndex): string[] {
    const toIndex: string[] = [];

    function walk(nodeList: FileNode[]) {
        for (const node of nodeList) {
            if (node.kind === 'file') {
                // 跳过 .ai_index.json 自身和隐藏文件
                if (node.name.startsWith('.')) continue;
                // 跳过压缩包（Gemini 无法解析）
                const ext = node.name.split('.').pop()?.toLowerCase() || '';
                if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) continue;
                const existing = existingIndex.files[node.path];
                if (!existing) {
                    // 新文件，需要索引
                    toIndex.push(node.path);
                } else if (
                    node.lastModified &&
                    existing.fileLastModified &&
                    node.lastModified > existing.fileLastModified
                ) {
                    // 文件自上次索引后有修改
                    toIndex.push(node.path);
                }
            } else if (node.children) {
                walk(node.children);
            }
        }
    }

    walk(nodes);
    return toIndex;
}

/**
 * 构建文件路径到 lastModified 的映射
 */
export function getFileLastModifiedMap(nodes: FileNode[]): Record<string, number> {
    const map: Record<string, number> = {};
    function walk(nodeList: FileNode[]) {
        for (const node of nodeList) {
            if (node.kind === 'file' && node.lastModified) {
                map[node.path] = node.lastModified;
            } else if (node.children) {
                walk(node.children);
            }
        }
    }
    walk(nodes);
    return map;
}

// ─── AI Agent File Operations ──────────────

/**
 * 通过路径导航到父目录的 DirectoryHandle
 * 返回 { parentHandle, fileName } 以便在该目录上执行操作
 */
async function navigateToParent(
    dirHandle: FileSystemDirectoryHandle,
    filePath: string
): Promise<{ parentHandle: FileSystemDirectoryHandle; fileName: string } | null> {
    try {
        const parts = filePath.split('/').filter(Boolean);
        if (parts.length === 0) return null;

        let currentHandle = dirHandle;
        // 遍历到倒数第二层（父目录）
        for (let i = 0; i < parts.length - 1; i++) {
            currentHandle = await currentHandle.getDirectoryHandle(parts[i]);
        }

        return {
            parentHandle: currentHandle,
            fileName: parts[parts.length - 1],
        };
    } catch (err) {
        console.error(`[useFileSystem] navigateToParent failed for: ${filePath}`, err);
        return null;
    }
}

/**
 * 通过路径重命名本地文件（支持嵌套目录）
 * 在同一父目录下创建新文件 → 复制内容 → 删除旧文件
 */
export async function renameFileByPath(
    dirHandle: FileSystemDirectoryHandle,
    oldPath: string,
    newName: string
): Promise<{ success: boolean; error?: string }> {
    try {
        const nav = await navigateToParent(dirHandle, oldPath);
        if (!nav) return { success: false, error: 'File not found' };

        const { parentHandle, fileName } = nav;

        // 读取旧文件内容（二进制安全）
        const oldFileHandle = await parentHandle.getFileHandle(fileName);
        const file = await oldFileHandle.getFile();
        const content = await file.arrayBuffer();

        // 创建新文件并写入
        const newHandle = await parentHandle.getFileHandle(newName, { create: true });
        // @ts-ignore
        const writable = await newHandle.createWritable();
        await writable.write(content);
        await writable.close();

        // 删除旧文件
        // @ts-ignore
        await parentHandle.removeEntry(fileName);

        console.log(`[useFileSystem] ✅ Renamed: ${oldPath} → ${newName}`);
        return { success: true };
    } catch (err: any) {
        console.error(`[useFileSystem] renameFileByPath error:`, err);
        return { success: false, error: err?.message || 'Rename failed' };
    }
}

/**
 * 创建本地文件夹（支持嵌套路径级联创建）
 * 例如 "A/B/C" 会依次创建 A → A/B → A/B/C
 */
export async function createLocalFolderByPath(
    dirHandle: FileSystemDirectoryHandle,
    folderPath: string
): Promise<{ success: boolean; error?: string }> {
    try {
        const parts = folderPath.split('/').filter(Boolean);
        if (parts.length === 0) return { success: false, error: 'Empty folder path' };

        let currentHandle = dirHandle;
        for (const part of parts) {
            currentHandle = await currentHandle.getDirectoryHandle(part, { create: true });
        }

        console.log(`[useFileSystem] ✅ Created folder: ${folderPath}`);
        return { success: true };
    } catch (err: any) {
        console.error(`[useFileSystem] createLocalFolderByPath error:`, err);
        return { success: false, error: err?.message || 'Failed to create folder' };
    }
}

/**
 * 移动一个或多个文件到指定的已存在文件夹中（原子移动工具）
 * 不负责创建目标文件夹。如果目标不存在则报错。
 */
export async function moveLocalFiles(
    dirHandle: FileSystemDirectoryHandle,
    sourcePaths: string[],
    destinationFolder: string
): Promise<{ success: boolean; moved: string[]; errors: string[] }> {
    const moved: string[] = [];
    const errors: string[] = [];

    try {
        // 获取目标文件夹的 handle（不创建，必须已经存在）
        let destHandle: FileSystemDirectoryHandle = dirHandle;
        const destParts = destinationFolder.split('/').filter(Boolean);
        for (const part of destParts) {
            try {
                destHandle = await destHandle.getDirectoryHandle(part);
            } catch {
                return { success: false, moved: [], errors: [`Destination folder "${destinationFolder}" does not exist. Use create_local_folder first.`] };
            }
        }

        // 逐个移动文件（二进制安全）
        for (const filePath of sourcePaths) {
            try {
                const nav = await navigateToParent(dirHandle, filePath);
                if (!nav) {
                    errors.push(`${filePath}: not found`);
                    continue;
                }

                const { parentHandle, fileName } = nav;

                // 读取旧文件
                const fileHandle = await parentHandle.getFileHandle(fileName);
                const file = await fileHandle.getFile();
                const content = await file.arrayBuffer();

                // 在目标文件夹中创建文件
                const newFileHandle = await destHandle.getFileHandle(fileName, { create: true });
                // @ts-ignore
                const writable = await newFileHandle.createWritable();
                await writable.write(content);
                await writable.close();

                // 从原位置删除
                // @ts-ignore
                await parentHandle.removeEntry(fileName);

                moved.push(filePath);
                console.log(`[useFileSystem] 📦 Moved: ${filePath} → ${destinationFolder}/${fileName}`);
            } catch (err: any) {
                errors.push(`${filePath}: ${err?.message || 'move failed'}`);
            }
        }
    } catch (err: any) {
        errors.push(`General error: ${err?.message || 'unknown'}`);
    }

    return {
        success: errors.length === 0,
        moved,
        errors,
    };
}

/**
 * 创建新文件夹并将指定文件移动到其中
 * @deprecated Use createLocalFolderByPath + moveLocalFiles instead
 */
export async function createFolderAndMove(
    dirHandle: FileSystemDirectoryHandle,
    folderName: string,
    filePaths: string[]
): Promise<{ success: boolean; moved: string[]; errors: string[] }> {
    const moved: string[] = [];
    const errors: string[] = [];

    try {
        // 在根目录创建新文件夹
        const newFolderHandle = await dirHandle.getDirectoryHandle(folderName, { create: true });
        console.log(`[useFileSystem] 📁 Created folder: ${folderName}`);

        // 逐个移动文件
        for (const filePath of filePaths) {
            try {
                const nav = await navigateToParent(dirHandle, filePath);
                if (!nav) {
                    errors.push(`${filePath}: not found`);
                    continue;
                }

                const { parentHandle, fileName } = nav;

                // 读取旧文件（二进制安全）
                const fileHandle = await parentHandle.getFileHandle(fileName);
                const file = await fileHandle.getFile();
                const content = await file.arrayBuffer();

                // 在新文件夹中创建文件
                const newFileHandle = await newFolderHandle.getFileHandle(fileName, { create: true });
                // @ts-ignore
                const writable = await newFileHandle.createWritable();
                await writable.write(content);
                await writable.close();

                // 从原位置删除
                // @ts-ignore
                await parentHandle.removeEntry(fileName);

                moved.push(filePath);
                console.log(`[useFileSystem] 📦 Moved: ${filePath} → ${folderName}/${fileName}`);
            } catch (err: any) {
                errors.push(`${filePath}: ${err?.message || 'move failed'}`);
            }
        }

        return { success: errors.length === 0, moved, errors };
    } catch (err: any) {
        console.error(`[useFileSystem] createFolderAndMove error:`, err);
        return { success: false, moved, errors: [`Failed to create folder: ${err?.message}`] };
    }
}

/**
 * 通过路径写入文件（支持自动创建中间目录）
 */
export async function writeFileByPath(
    dirHandle: FileSystemDirectoryHandle,
    filePath: string,
    content: string
): Promise<{ success: boolean; error?: string }> {
    try {
        const parts = filePath.split('/').filter(Boolean);
        let currentHandle = dirHandle;

        // 自动创建中间目录
        for (let i = 0; i < parts.length - 1; i++) {
            currentHandle = await currentHandle.getDirectoryHandle(parts[i], { create: true });
        }

        const fileName = parts[parts.length - 1];
        const fileHandle = await currentHandle.getFileHandle(fileName, { create: true });

        // @ts-ignore
        const writable = await fileHandle.createWritable();
        await writable.write(content);
        await writable.close();

        console.log(`[useFileSystem] ✅ Written: ${filePath} (${content.length} chars)`);
        return { success: true };
    } catch (err: any) {
        console.error(`[useFileSystem] writeFileByPath error:`, err);
        return { success: false, error: err?.message || 'Write failed' };
    }
}

/**
 * 通过路径删除本地文件
 */
export async function deleteFileByPath(
    dirHandle: FileSystemDirectoryHandle,
    filePath: string,
    confirmName: string
): Promise<{ success: boolean; error?: string }> {
    try {
        const nav = await navigateToParent(dirHandle, filePath);
        if (!nav) return { success: false, error: 'File not found' };

        const { parentHandle, fileName } = nav;

        // 安全检查：确认文件名匹配
        if (fileName !== confirmName) {
            return { success: false, error: `Confirmation name "${confirmName}" does not match filename "${fileName}"` };
        }

        // @ts-ignore
        await parentHandle.removeEntry(fileName);

        console.log(`[useFileSystem] 🗑️ Deleted: ${filePath}`);
        return { success: true };
    } catch (err: any) {
        console.error(`[useFileSystem] deleteFileByPath error:`, err);
        return { success: false, error: err?.message || 'Delete failed' };
    }
}

/**
 * 读取文件为 Base64 字符串（用于二进制文件上传到 Gemini）
 * 同时返回 MIME 类型
 */
export async function readFileAsBase64(
    dirHandle: FileSystemDirectoryHandle,
    filePath: string
): Promise<{ base64: string; mimeType: string; fileName: string; size: number } | null> {
    try {
        const parts = filePath.split('/').filter(Boolean);
        let currentHandle: FileSystemDirectoryHandle = dirHandle;

        for (let i = 0; i < parts.length; i++) {
            const part = parts[i];
            if (i === parts.length - 1) {
                // 最后一个是文件
                const fileHandle = await currentHandle.getFileHandle(part);
                const file = await fileHandle.getFile();
                const buffer = await file.arrayBuffer();

                // ArrayBuffer → Base64
                const bytes = new Uint8Array(buffer);
                let binary = '';
                for (let j = 0; j < bytes.length; j++) {
                    binary += String.fromCharCode(bytes[j]);
                }
                const base64 = btoa(binary);

                return {
                    base64,
                    mimeType: file.type || guessMimeType(file.name),
                    fileName: file.name,
                    size: file.size,
                };
            } else {
                currentHandle = await currentHandle.getDirectoryHandle(part);
            }
        }
        return null;
    } catch (err) {
        console.error(`[useFileSystem] readFileAsBase64 error for: ${filePath}`, err);
        return null;
    }
}

/**
 * 根据文件扩展名推断 MIME 类型
 */
function guessMimeType(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const mimeMap: Record<string, string> = {
        pdf: 'application/pdf',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xls: 'application/vnd.ms-excel',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        ppt: 'application/vnd.ms-powerpoint',
        pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        mp3: 'audio/mpeg',
        wav: 'audio/wav',
        mp4: 'video/mp4',
        mov: 'video/quicktime',
        avi: 'video/x-msvideo',
        webm: 'video/webm',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        webp: 'image/webp',
        svg: 'image/svg+xml',
    };
    return mimeMap[ext] || 'application/octet-stream';
}

/**
 * 判断文件是否为二进制格式（需要用 Gemini 解析）
 */
export function isBinaryFile(fileName: string): boolean {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    const binaryExts = [
        'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
        'mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a',
        'mp4', 'mov', 'avi', 'webm', 'mkv',
        'jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'tiff',
        'zip', 'rar', '7z', 'tar', 'gz',
    ];
    return binaryExts.includes(ext);
}

/**
 * 判断文件名是否已有足够描述性（不需要 AI 标签）
 * 乱码/数字/哈希命名 → false，需要 AI
 * 中文/英文有意义名称 → true，跳过 AI
 */
export function isDescriptiveFileName(fileName: string): boolean {
    // 去掉扩展名
    const dotIdx = fileName.lastIndexOf('.');
    const stem = dotIdx > 0 ? fileName.substring(0, dotIdx) : fileName;

    // 纯数字（如 1234567）
    if (/^\d+$/.test(stem)) return false;
    // UUID 风格（如 a1b2c3d4-e5f6-7890）
    if (/^[0-9a-f-]{20,}$/i.test(stem)) return false;
    // 时间戳/相机命名（如 IMG_20240315、VID_20240315_143022、DSC00123）
    if (/^(IMG|VID|DSC|DCIM|WX|wx|Screenshot|Screen Shot|录屏|截屏)[_\-\s]?\d/i.test(stem)) return false;
    // 太短且无中日韩字符（如 doc1、a、tmp）
    if (stem.length <= 3 && !/[\u4e00-\u9fff\u3040-\u30ff\uac00-\ud7af]/.test(stem)) return false;
    // 随机哈希风格（如 f7a2b3c、tmp_xk29f）— 字母数字混合无空格无中文
    if (/^[a-z0-9_-]+$/i.test(stem) && !/[\u4e00-\u9fff]/.test(stem) && stem.length < 10 && !/[aeiou]{2}/i.test(stem)) return false;

    // 含中日韩字符 → 有意义
    if (/[\u4e00-\u9fff\u3040-\u30ff\uac00-\ud7af]/.test(stem)) return true;
    // 含空格或下划线分隔的多个单词 → 有意义
    if (/[\s_-]/.test(stem) && stem.length >= 5) return true;
    // 纯英文单词且够长 → 有意义
    if (/^[a-zA-Z]{4,}/.test(stem) && stem.length >= 6) return true;

    return false;
}

/**
 * 检查浏览器是否支持 File System Access API
 */
export function isFileSystemSupported(): boolean {
    return 'showDirectoryPicker' in window;
}

/**
 * 判断文件是否为可编辑的文本文件
 */
export function isTextFile(fileName: string): boolean {
    const textExtensions = [
        'txt', 'md', 'json', 'js', 'ts', 'jsx', 'tsx', 'css', 'html', 'xml',
        'yaml', 'yml', 'toml', 'ini', 'cfg', 'conf', 'env', 'sh', 'bash',
        'py', 'rb', 'go', 'rs', 'java', 'c', 'cpp', 'h', 'hpp', 'cs',
        'php', 'sql', 'svg', 'csv', 'log', 'gitignore', 'dockerignore',
        'editorconfig', 'eslintrc', 'prettierrc', 'babelrc',
    ];
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    // 无扩展名的常见配置文件
    const noExtNames = ['Dockerfile', 'Makefile', 'Procfile', 'LICENSE', 'README'];
    return textExtensions.includes(ext) || noExtNames.includes(fileName);
}

/**
 * 获取文件类型的图标表情
 */
export function getFileEmoji(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    if (['pdf'].includes(ext)) return '📄';
    if (['doc', 'docx'].includes(ext)) return '📝';
    if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
    if (['ppt', 'pptx'].includes(ext)) return '📊';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) return '🖼️';
    if (['mp4', 'avi', 'mov', 'webm'].includes(ext)) return '🎬';
    if (['mp3', 'wav', 'flac', 'aac'].includes(ext)) return '🎵';
    if (['zip', 'tar', 'gz', 'rar', '7z'].includes(ext)) return '📦';
    if (['js', 'ts', 'jsx', 'tsx', 'py', 'rb', 'go', 'rs', 'java', 'c', 'cpp'].includes(ext)) return '💻';
    if (['json', 'yaml', 'yml', 'toml', 'xml'].includes(ext)) return '⚙️';
    if (['md', 'txt'].includes(ext)) return '📃';
    return '📄';
}
